/* Attendance — heads on the floor day by day, by day of the week, and the
 * daily register underneath. */

import { SK } from '../../lib/api_attendance'
import type { AttMonth } from '../../lib/api_attendance'
import { PanelHead } from '../../components/ui'
import { num } from '../../lib/format'
import type { Scope } from './model'
import { BAD, Foot, OK, R, Sheet, Sub, Track, pal, signed } from './parts'
import type { People } from './parts'

interface Props {
  D: AttMonth; S: Scope; target: number; dark: boolean; people: People
  setTarget: (v: number) => void
}

export function DaysTab(props: Props) {
  return (
    <>
      <DayByDay {...props} />
      <DayOfWeek {...props} />
      <Register {...props} />
    </>
  )
}

// ----------------------------------------------------------- day by day
function DayByDay({ D, S, target, dark, setTarget }: Props) {
  const P = pal(dark)
  const { sc, req, isAll, sel } = S
  const maxDay = Math.max(req ?? 0, ...sc.map((x) => x.p)) * 1.08 || 1
  const bw = 1000 / S.nDays
  const TICKS = 5
  const grid = Array.from({ length: TICKS + 1 }, (_, i) => Math.round(300 - (i / TICKS) * 300))
  const yLabels = Array.from({ length: TICKS + 1 }, (_, i) => num((maxDay * i) / TICKS))

  const bars = sc.map((d, i) => {
    let acc = 0
    const segs = SK.map((k, si) => {
      const h = (d.s[k] / maxDay) * 300
      const y = 300 - acc - h
      acc += h
      return { k, y, h: Math.max(h, 0), fill: P.shift[si] }
    })
    return {
      d, x: i * bw + bw * 0.14, w: bw * 0.72, segs,
      title: `${d.d} ${D.mon} ${d.dow} · ${num(d.p)} present — `
        + SK.map((k) => `${k === 'O' ? '12-hr/other' : k === 'G' ? 'Gen' : k} ${num(d.s[k])}`).join(', '),
    }
  })
  const reqY = req ? 300 - ((req * target) / 100 / maxDay) * 300 : null
  const legend = SK.map((k, i) => ({
    label: k === 'O' ? '12-hr / other' : k === 'G' ? 'General' : 'Shift ' + k, color: P.shift[i],
  }))
  const cols = '38px minmax(0,1fr)'

  return (
    <Sheet>
      <PanelHead title="Heads on the floor, day by day" right={
        <>
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="axis-title">Attendance target</span>
            <input type="range" min={50} max={100} step={1} value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
              style={{ width: 150, margin: 0 }} />
            <span className="num" style={{
              fontSize: 14, minWidth: 44, color: 'var(--color-accent-700)',
            }}>{num(target)}%</span>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {legend.map((l) => (
              <div key={l.label} style={{
                display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
                color: 'var(--color-neutral-800)',
              }}>
                <span style={{ width: 14, height: 10, display: 'block', background: l.color }} />
                {l.label}
              </div>
            ))}
          </div>
        </>
      } />
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, marginTop: 20 }}>
        <div className="num" style={{
          display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between',
          alignItems: 'flex-end', height: 300, margin: '-7px 0', fontSize: 11,
          color: 'var(--color-neutral-600)',
        }}>
          {yLabels.map((t, i) => <span key={i}>{t}</span>)}
        </div>
        <svg viewBox="0 0 1000 300" preserveAspectRatio="none" style={{
          width: '100%', height: 300, display: 'block',
          borderLeft: '1px solid var(--color-neutral-500)',
          borderBottom: '1px solid var(--color-neutral-500)',
        }}>
          {grid.map((y) => (
            <line key={y} x1="0" y1={y} x2="1000" y2={y} stroke="var(--c-grid)" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
          ))}
          {bars.map((b) => (
            <g key={b.d.d}>
              <title>{b.title}</title>
              {b.segs.map((s) => (
                <rect key={s.k} x={b.x.toFixed(1)} y={s.y.toFixed(1)} width={b.w.toFixed(1)}
                  height={s.h.toFixed(1)} fill={s.fill} />
              ))}
            </g>
          ))}
          {reqY !== null ? (
            <line x1="0" y1={reqY.toFixed(1)} x2="1000" y2={reqY.toFixed(1)} stroke="var(--c-ink)"
              strokeWidth="1.6" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
          ) : null}
        </svg>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8 }}>
        <div />
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(${S.nDays}, minmax(0,1fr))`,
          fontSize: 10, color: 'var(--color-neutral-600)', marginTop: 5, textAlign: 'center',
        }}>
          {sc.map((d) => <span key={d.d}>{d.d % 2 === 1 || sc.length <= 16 ? d.d : ''}</span>)}
        </div>
      </div>
      <Foot top={16}>
        {isAll ? '' : sel + ' only. '}
        Bars stack the four sanctioned shifts plus 12-hour duty and other codes.{' '}
        {req
          ? `The dashed line sits at the attendance target — ${num(target)}% of the ${num(req)}-head requirement, so ${num((req * target) / 100)} heads. Move the slider to see what a stricter or looser target demands; the gap below the line is the shortfall the mill absorbs through twelve-hour days and slower machine manning.`
          : `The ${D.requirementSheet} sheet carries no sanctioned strength for this department, so there is no requirement line to read against.`}
      </Foot>
    </Sheet>
  )
}

// -------------------------------------------------------- day of week
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function DayOfWeek({ S, target }: Props) {
  const { sc, req, isAll, sel } = S
  const agg = new Map<string, { n: number; p: number; l: number }>()
  sc.forEach((d) => {
    const o = agg.get(d.dow) ?? { n: 0, p: 0, l: 0 }
    o.n++; o.p += d.p; o.l += d.l
    agg.set(d.dow, o)
  })
  const rows = DOW.filter((k) => agg.has(k)).map((k) => {
    const o = agg.get(k)!
    return { label: k, avg: o.p / o.n, late: o.p ? (o.l / o.p) * 100 : null }
  })
  const max = Math.max(...rows.map((r) => r.avg), 1)
  const sorted = [...rows].sort((a, b) => a.avg - b.avg)
  const thin = sorted[0], strong = sorted[sorted.length - 1]
  const spread = strong && strong.avg ? ((strong.avg - thin.avg) / strong.avg) * 100 : 0
  const short = req !== null && S.presentAvg < req

  return (
    <Sheet>
      <PanelHead title="By day of the week" />
      <Sub>Average heads present{rows.length === 7 ? ' · the mill runs every day' : ''}</Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
        {rows.map((r) => (
          <div key={r.label} style={{
            display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) 150px',
            gap: 14, alignItems: 'center',
          }}>
            <div style={{ fontSize: 13, color: 'var(--color-neutral-800)' }}>{r.label}</div>
            <Track width={300} height={16} value={Math.round((r.avg / max) * 300)}
              fill={req && (r.avg / req) * 100 < target ? BAD : 'var(--c-bar1)'} />
            <div className="num" style={{
              fontSize: 13, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end',
            }}>
              <span>
                {num(r.avg, 1)}
                <span style={{ color: 'var(--color-neutral-500)' }}>{req ? '/' + num(req) : ''}</span>
              </span>
              <span style={{
                color: r.late === null ? 'var(--color-neutral-600)'
                  : r.late > 38 ? BAD : r.late < 30 ? OK : 'var(--color-neutral-700)',
              }}>{r.late === null ? '—' : num(r.late, 0) + '% late'}</span>
            </div>
          </div>
        ))}
      </div>
      {thin ? (
        <Foot wide={false}>
          {isAll ? '' : sel + ' only. '}
          Late share is red above 38% of that day’s present heads and green below 30%, against
          {isAll ? ' a plant' : ' an'} average of {num(S.latePct, 0)}%. Thinnest day
          is {thin.label} at {num(thin.avg, 1)} heads, strongest {strong.label} at {num(strong.avg, 1)}.{' '}
          {spread < 10
            ? 'The spread across the week is narrow' + (short
              ? ', so the shortfall is structural rather than a weekend-absence pattern.'
              : '.')
            : `The spread across the week is ${num(spread, 0)}% of the strongest day — wide enough to look for a day-of-the-week pattern.`}
        </Foot>
      ) : null}
    </Sheet>
  )
}

// ------------------------------------------------------- daily register
function Register({ D, S, people }: Props) {
  const { sc, req } = S
  const muted = { ...R, color: 'var(--color-neutral-700)' }
  return (
    <Sheet>
      <PanelHead title={people === 'staff' ? 'Daily report' : 'Daily register'} />
      <div className="scroll-x" style={{ marginTop: 14 }}>
        <table className="table" style={{ width: '100%', minWidth: 760 }}>
          <thead>
            <tr>
              <th>Date</th><th style={R}>Present</th><th style={R}>Short of req.</th>
              <th style={R}>A</th><th style={R}>B</th><th style={R}>C</th><th style={R}>Gen</th>
              <th style={R}>12-hr</th><th style={R}>Late</th><th style={R}>Punch misses</th>
              <th style={R}>OT hrs</th><th style={R}>Avg hrs</th>
            </tr>
          </thead>
          <tbody>
            {sc.map((d) => (
              <tr key={d.d}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {d.d} {D.mon}<span style={{ color: 'var(--color-neutral-600)' }}> {d.dow}</span>
                </td>
                <td className="num" style={R}>{num(d.p)}</td>
                <td className="num" style={{
                  ...R, color: req ? (req - d.p > 0 ? BAD : OK) : 'var(--color-neutral-600)',
                }}>{req ? signed(d.p - req) : '—'}</td>
                <td className="num" style={muted}>{num(d.s.A)}</td>
                <td className="num" style={muted}>{num(d.s.B)}</td>
                <td className="num" style={muted}>{num(d.s.C)}</td>
                <td className="num" style={muted}>{num(d.s.G)}</td>
                <td className="num" style={muted}>{num(d.s.O)}</td>
                <td className="num" style={R}>{num(d.l)}</td>
                <td className="num" style={R}>{num(d.m)}</td>
                <td className="num" style={R}>{num(d.ot)}</td>
                <td className="num" style={R}>{d.h ? num(d.h, 1) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Foot top={14}>
        12-hr is 12-hour duty, other shift codes and days with no shift code.{' '}
        {people === 'staff'
          ? 'Staff have no register, so there are no punch misses to count and no PP marks to read overtime off; both columns stay at nil.'
          : `Punch misses are days the register marks present without both punches on the biometric. OT hrs are ${D.otHours.PP} for each PP mark in the register and ${D.otHours.PPP} for a PPP.`}
        {' '}Avg hrs is in-punch to out-punch, over the present days that have both.
      </Foot>
    </Sheet>
  )
}
